import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Receipt, PencilSimple } from "@phosphor-icons/react";
import { parseNum, fmtDate } from "@/lib/utils";
import { PasswordConfirmDialog } from "@/components/PasswordConfirmDialog";
import { AuditLogCard } from "@/components/AuditLogCard";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { BR_STATES } from "@/lib/br";

const EMPTY = { trip_id: "", category: "pedagio", description: "", date: "", unit_value: "", quantity: 1, total: 0, payment_method: "dinheiro", paid_by: "motorista", establishment: "", state: "", city: "" };
const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [pwAction, setPwAction] = useState(null);

  const load = async () => { const [e, t] = await Promise.all([api.get("/expenses"), api.get("/trips")]); setItems(e.data); setTrips(t.data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail?.entity === "expense") load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  const set = (k, isNum = false) => (e) => {
    const raw = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: raw }));
  };

  // Auto-calc total
  useEffect(() => {
    const total = parseNum(form.unit_value) * parseNum(form.quantity);
    setForm((f) => ({ ...f, total }));
    // eslint-disable-next-line
  }, [form.unit_value, form.quantity]);

  const EXP_NUM_FIELDS = ["unit_value", "quantity"];
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    EXP_NUM_FIELDS.forEach((k) => { payload[k] = parseNum(payload[k]); });
    payload.total = parseNum(payload.total);
    if (editingId) {
      setPwAction({ kind: "edit", id: editingId, payload });
    } else {
      try { await api.post("/expenses", payload); toast.success("Despesa registrada"); setOpen(false); setForm(EMPTY); load(); }
      catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    }
  };
  const startEdit = (item) => { setForm({ ...EMPTY, ...item }); setEditingId(item.id); setOpen(true); };
  const askDelete = (id) => setPwAction({ kind: "delete", id });
  const runPwAction = async () => {
    if (!pwAction) return;
    try {
      if (pwAction.kind === "delete") {
        await api.delete(`/expenses/${pwAction.id}`);
        toast.success("Despesa removida");
      } else if (pwAction.kind === "edit") {
        await api.put(`/expenses/${pwAction.id}`, pwAction.payload);
        toast.success("Despesa atualizada");
        setOpen(false);
      }
      setPwAction(null); 
      window.dispatchEvent(new CustomEvent("audit-log-refresh", { detail: { entity: "expense" } }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <Receipt size={32} weight="duotone" className="text-blue-600" /> Despesas de Viagem
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Pedágios sem tag, estacionamentos e outros</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} data-testid="btn-new-expense" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Plus size={16} /> Nova Despesa</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar Despesa (requer senha)" : "Nova Despesa"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Viagem</Label>
                <Select value={form.trip_id} onValueChange={set("trip_id")}>
                  <SelectTrigger><SelectValue placeholder="Selecione a viagem" /></SelectTrigger>
                  <SelectContent>{trips.map(t => <SelectItem key={t.id} value={t.id}>{t.origin} → {t.destination}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pedagio">Pedágio (sem tag)</SelectItem><SelectItem value="estacionamento">Estacionamento/Pernoite</SelectItem><SelectItem value="alimentacao">Alimentação</SelectItem><SelectItem value="outros">Outros</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.date} onChange={set("date")} /></div>
              <div className="col-span-2"><Label>Descrição</Label><Input value={form.description} onChange={set("description")} /></div>
              <div><Label>Valor unit.</Label><Input type="text" inputMode="decimal" placeholder="ex: 15,50" value={form.unit_value} onChange={set("unit_value", true)} data-testid="expense-unit-value" /></div>
              <div><Label>Qtd</Label><Input type="text" inputMode="decimal" value={form.quantity} onChange={set("quantity", true)} data-testid="expense-quantity" /></div>
              <div className="col-span-2"><Label>Total (auto)</Label><Input readOnly value={fmt(form.total)} className="bg-blue-50 font-bold" data-testid="expense-total" /></div>
              <div><Label>Forma pagto</Label>
                <Select value={form.payment_method} onValueChange={set("payment_method")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dinheiro">Dinheiro</SelectItem><SelectItem value="pix">PIX</SelectItem><SelectItem value="cartao">Cartão</SelectItem><SelectItem value="carta_frete">Carta Frete</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Pago por</Label>
                <Select value={form.paid_by} onValueChange={set("paid_by")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="motorista">Motorista</SelectItem><SelectItem value="empresa">Empresa</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-1 md:col-span-2"><Label>Estabelecimento</Label>
                <Input value={form.establishment} onChange={set("establishment")} placeholder="Ex: Posto Trevão, Restaurante do Zé" data-testid="expense-establishment" />
              </div>
              <div><Label>UF</Label>
                <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v, city: "" }))}>
                  <SelectTrigger data-testid="expense-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Cidade</Label>
                <CityAutocomplete value={form.city} uf={form.state} onChange={set("city")} placeholder="Digite a cidade" data-testid="expense-city" />
              </div>
              <DialogFooter className="col-span-1 md:col-span-2"><Button type="submit" data-testid="btn-save-expense" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Salvar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Despesas ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-muted-foreground text-sm">Nenhuma despesa registrada.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Categoria</TableHead><TableHead>Descrição</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Pago por</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="text-xs">{fmtDate(i.date)}</TableCell>
                    <TableCell className="capitalize">{i.category}</TableCell>
                    <TableCell>{i.description}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(i.total)}</TableCell>
                    <TableCell className="text-xs capitalize">{i.paid_by}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(i)} data-testid={`btn-edit-exp-${i.id}`} title="Editar (requer senha)"><PencilSimple size={16} className="text-blue-600" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => askDelete(i.id)} data-testid={`btn-del-exp-${i.id}`} title="Excluir (requer senha)"><Trash size={16} className="text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PasswordConfirmDialog
        open={!!pwAction}
        onOpenChange={(v) => !v && setPwAction(null)}
        action={pwAction?.kind === "delete" ? "exclusão" : "edição"}
        description={pwAction?.kind === "delete"
          ? "Esta despesa será removida permanentemente. Confirme sua senha para continuar."
          : "Você está prestes a editar uma despesa salva. Confirme sua senha para aplicar as mudanças."}
        variant={pwAction?.kind === "delete" ? "destructive" : "default"}
        onConfirm={runPwAction}
      />

      <AuditLogCard entity="expense" title="Histórico de alterações em Despesas" />
    </div>
  );
}
